import { useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router";
import { SidebarTrigger, useSidebar } from "@/lib/components/Sidebar";
import { useDfyOrderStore } from "@/lib/stores/dfy-order-store/dfy-order-store";
import { AnalyticsApi } from "@/lib/api/analytics-api";
import {
  DFY_FORM_STEPS,
  DfyOrderFormStepId,
  DfyStepProps,
} from "./dfy-order-form-steps";
import PackageStep from "./steps/PackageStep";
import AddOnsStep from "./steps/AddOnsStep";
import ImagesStep from "./steps/ImagesStep";
import BrandingStep from "./steps/BrandingStep";
import ReviewStep from "./steps/ReviewStep";
import DfyStepper from "./components/DfyStepper";
import DfyStickyCostBreakdown from "./components/DfyStickyCostBreakdown";

const STEP_COMPONENTS: Record<
  DfyOrderFormStepId,
  React.ComponentType<DfyStepProps>
> = {
  package: PackageStep,
  addons: AddOnsStep,
  images: ImagesStep,
  branding: BrandingStep,
  review: ReviewStep,
};

export default function DfyOrderFormShell() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isMobile } = useSidebar();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const teamExternalId = useDfyOrderStore((state) => state.teamExternalId);
  const orderExternalId = useDfyOrderStore((state) => state.orderExternalId);
  const details = useDfyOrderStore((state) => state.order!.details);
  const hasBranding = details.addOns.branding;

  const steps = useMemo(
    () =>
      DFY_FORM_STEPS.filter((step) => step.id !== "branding" || hasBranding),
    [hasBranding],
  );
  const requestedId = searchParams.get("step");
  const currentIndex = Math.max(
    0,
    steps.findIndex((step) => step.id === requestedId),
  );
  const currentStep = steps[currentIndex]!;
  const previousStep = steps[currentIndex - 1];
  const nextStep = steps[currentIndex + 1];

  const goToStep = (stepId: DfyOrderFormStepId) => {
    const targetIndex = steps.findIndex((step) => step.id === stepId);
    if (targetIndex === currentIndex) {
      return;
    }

    void AnalyticsApi.logDfyOrderFormStepChanged(
      {
        orderExternalId,
        fromStep: currentStep.id,
        toStep: stepId,
        direction: targetIndex > currentIndex ? "FORWARD" : "BACKWARD",
        details,
      },
      teamExternalId,
    );

    setSearchParams(
      (params) => {
        params.set("step", stepId);
        return params;
      },
      { replace: true },
    );
  };

  useEffect(() => {
    scrollContainerRef.current?.scrollTo({ top: 0 });
  }, [currentStep.id]);

  const StepComponent = STEP_COMPONENTS[currentStep.id];

  return (
    <div
      ref={scrollContainerRef}
      className="@container flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto"
    >
      <div className="mx-auto flex w-full max-w-3xl flex-col px-4 pt-4 pb-18 sm:px-6 sm:pt-16 sm:pb-15 @5xl:max-w-[68rem]">
        {isMobile && (
          <div className="mb-6 flex">
            <SidebarTrigger />
          </div>
        )}
        <div className="grid grid-cols-1 @5xl:grid-cols-[minmax(0,1fr)_18rem] @5xl:gap-x-10 @5xl:[&>:not(aside)]:col-start-1">
          <DfyStepper
            steps={steps}
            currentIndex={currentIndex}
            onStepClick={goToStep}
          />
          <StepComponent
            onBack={previousStep ? () => goToStep(previousStep.id) : undefined}
            onNext={nextStep ? () => goToStep(nextStep.id) : undefined}
          />
          <aside className="col-start-2 row-start-3 hidden pt-6 @5xl:block">
            {currentStep.id !== "review" && (
              <div className="sticky top-6">
                <DfyStickyCostBreakdown showTitle={false} />
              </div>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}
